import styled, { css } from 'styled-components'
import { t } from 'ttag'
import { useCallback, useContext } from 'react'

import { Globals } from '@h2/globals'
import { Text } from '@h2/components/typography/text'
import { LogoutIcon } from '@h2/components/icons/logout'
import { PB70T, SLB, WHITE, PB } from '@h2/components/colors'
import { Button } from '@h2/components/buttons/button'
import { museoFont } from '@h2/components/mixins'
import { useIsUserOver13 } from '@h2/common/components/user/checkUser'

import { subNavList, navLink } from '../navigation/mixins'
import { Link as LinkComponent } from '../../link'

const Nav = styled.nav`
  position: relative;

  > ul {
    padding: ${({ isEarlyLearnerMode }) => (isEarlyLearnerMode ? '10px 0' : '0')};
  }
`
const Link = styled(LinkComponent)`
  ${navLink};
  flex-direction: row;

  > span[role='img'] {
    margin-right: 8px;
  }
`
const earlyLearnerModeStyles = css`
  > a,
  > .switch-to-advanced-view-button {
    background-color: ${SLB};

    span {
      ${museoFont}
      font-weight: 700;
      color: ${WHITE};
    }

    :hover {
      span {
        color: ${PB};
      }
    }
  }
`
const List = styled.ul`
  ${subNavList};
  left: auto;
  right: 0;
  flex-direction: column;
  width: auto;
  box-shadow: 0 3px 6px ${PB70T};
  border-radius: ${({ isEarlyLearnerMode }) => (isEarlyLearnerMode ? '10px' : '0')};
  background-color: ${({ isEarlyLearnerMode }) =>
    isEarlyLearnerMode ? SLB : 'transparent'};

  > li {
    width: auto;
    border: none;

    ${({ isEarlyLearnerMode }) => (isEarlyLearnerMode ? earlyLearnerModeStyles : null)}
  }
`
const Menu = () => {
  const {
    userProfile,
    isNavianceElementary,
    setEarlyDashboard,
    handleOnViewChange,
    isEarlyLearnerMode
  } = useContext(Globals)
  const isOver13 = useIsUserOver13()
  const shouldShowExit =
    isEarlyLearnerMode &&
    (!isNavianceElementary || (isNavianceElementary && userProfile.hasManager))
  const navianceElementaryStudentOrStaff = isEarlyLearnerMode || isNavianceElementary
  const handleToggle = useCallback(() => {
    if (isNavianceElementary) {
      return handleOnViewChange()
    }

    return setEarlyDashboard(false)
  }, [setEarlyDashboard, isNavianceElementary, handleOnViewChange])

  return (
    <Nav isEarlyLearnerMode={isEarlyLearnerMode}>
      <List isEarlyLearnerMode={isEarlyLearnerMode}>
        <li>
          <Link href="/personalprofile/edit">
            <Text as="span" size="medium">
              {t`Update Profile`}
            </Text>
          </Link>
        </li>
        <li>
          <Link href="/personalprofile/edit/password">
            <Text as="span" size="medium">
              {t`Change Password`}
            </Text>
          </Link>
        </li>
        <li>
          {isOver13 && (
            <Link href="/personalprofile/edit/management">
              <Text as="span" size="medium">
                {t`Review Management`}
              </Text>
            </Link>
          )}
        </li>
        {!navianceElementaryStudentOrStaff && (
          <li>
            <Link href="/personalprofile/edit/linkedAccounts">
              <Text as="span" size="medium">
                {t`Linked Accounts`}
              </Text>
            </Link>
          </li>
        )}
        {shouldShowExit && (
          <li>
            <Button
              className="switch-to-advanced-view-button"
              variant="text"
              size="medium"
              onClick={handleToggle}>
              <Text as="span" size="medium">
                {isNavianceElementary
                  ? t`Switch to Navigator Mode`
                  : t`Switch to Advanced View`}
              </Text>
            </Button>
          </li>
        )}
        <li>
          <Link ssr href="/logout">
            <LogoutIcon />
            <Text as="span" size="medium">
              {t`Logout`}
            </Text>
          </Link>
        </li>
      </List>
    </Nav>
  )
}

export { Menu }
